import type { TranslationKey } from '@/shared/i18n/types'
import type { MatchFixtureDto, RoundDto } from '../types'

/**
 * Pure round/fixture logic. Wording lives in the dictionary and formatting in
 * `shared/lib/formatDate` — this file decides *which* label applies, never what
 * it says, so it stays language-agnostic and cheap to test.
 */

// A league has one phase, so it contributes nothing to "Round N".
const PHASE_KEY: Record<string, TranslationKey | null> = {
  GROUP_STAGE:    'phase.groupStage',
  R32:            'phase.r32',
  R16:            'phase.r16',
  QF:             'phase.qf',
  SF:             'phase.sf',
  FINAL:          'phase.final',
  REGULAR_SEASON: null,
}

const LIVE_STATUSES = ['LIVE', 'HALFTIME']

export function isLiveStatus(status: string): boolean {
  return LIVE_STATUSES.includes(status)
}

/** The phase to append after "Round N", or null when the round needs no suffix. */
export function roundPhaseKey(round: RoundDto): TranslationKey | null {
  return PHASE_KEY[round.phase] ?? null
}

export function isRoundLive(round: RoundDto | null | undefined): boolean {
  return round?.matches.some((m) => isLiveStatus(m.status)) ?? false
}

export type MatchTimeState = 'postponed' | 'live' | 'scheduled'

/**
 * What belongs in a row's leading time slot: a state word when the match is not
 * simply waiting to kick off, otherwise the kickoff time itself.
 */
export function matchTimeState(match: MatchFixtureDto): MatchTimeState {
  if (match.status === 'POSTPONED') return 'postponed'
  if (isLiveStatus(match.status))   return 'live'
  return 'scheduled'
}
